import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Bot,
  Check,
  CheckCircle2,
  Circle,
  Copy,
  ExternalLink,
  Globe,
  Loader2,
  RefreshCw,
  SquareTerminal,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ModeToggle } from '@/components/mode-toggle';
import { useTheme } from '@/components/theme-provider';
import { IntegrationIcon } from '../components/IntegrationIcon';
import { apiClient } from '../api/client';
import {
  INTEGRATION_CATALOG,
  IntegrationDef,
  groupByCategory,
} from '../lib/integrationCatalog';
import { generateAgentPrompt, bareConnectCommand } from '../lib/agentOnboardingPrompt';
import { PageLoader } from '../components/PageLoader';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

type WizardStep = 'org' | 'pick' | 'method' | 'browser' | 'agent' | 'cli' | 'done';
type SetupMethod = 'browser' | 'agent' | 'cli';

// Raw onboarding API key survives page refreshes during setup, but is
// scoped to the tab session and cleared when the wizard completes.
const ONBOARDING_KEY_STORAGE = 'kestrel_onboarding_api_key';

// Status ids from GET /api/workflows/integrations/status keyed by catalog key
function statusIdFor(def: IntegrationDef): string {
  return def.statusId || def.key;
}

function CopyIconButton({ text, className }: { text: string; className?: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy to clipboard');
    }
  };
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleCopy}
      className={cn('h-7 w-7 p-0 flex-shrink-0', className)}
      aria-label={copied ? 'Copied' : 'Copy'}
      title={copied ? 'Copied' : 'Copy'}
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
    </Button>
  );
}

function CodeBlock({ children, copyText }: { children: string; copyText?: string }) {
  // Center the copy icon against single-line commands; pin it to the top
  // for multi-line blocks so it stays in the expected top-right position.
  const multiline = children.includes('\n');
  return (
    <div className={cn('flex gap-2 bg-muted rounded-md p-3', multiline ? 'items-start' : 'items-center')}>
      <pre className="flex-1 min-w-0 text-xs font-mono text-foreground overflow-x-auto whitespace-pre-wrap break-all">
        {children}
      </pre>
      {copyText !== undefined && (
        <CopyIconButton text={copyText} className={multiline ? undefined : '-my-1.5'} />
      )}
    </div>
  );
}

export function SetupWizard() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDarkMode =
    theme === 'dark' ||
    (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  const [step, setStep] = useState<WizardStep>('pick');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [method, setMethod] = useState<SetupMethod | null>(null);
  const [connectedIds, setConnectedIds] = useState<Set<string>>(new Set());
  const [statusLoading, setStatusLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [validated, setValidated] = useState(false);
  // Non-null when the org still has its randomly generated placeholder name
  // (personal-email signups) — the wizard then opens with a naming step
  // pre-filled with that name so the user can optionally pick their own.
  const [defaultOrgName, setDefaultOrgName] = useState<string | null>(null);
  // Shared between the CLI and agent steps so switching methods reuses the
  // same onboarding key instead of minting duplicates. Persisted in
  // sessionStorage so a page refresh mid-setup doesn't force the user to
  // create another key; cleared when setup completes or the tab closes.
  const [apiKey, setApiKey] = useState<string | null>(
    () => sessionStorage.getItem(ONBOARDING_KEY_STORAGE) || null
  );
  const [creatingKey, setCreatingKey] = useState(false);

  const createKey = async () => {
    setCreatingKey(true);
    try {
      const key = await apiClient.createApiKey({
        name: 'Onboarding',
        scopes: ['integrations:read', 'integrations:manage', 'workflows:read', 'workflows:write', 'catalog:read'],
        expires_in: '30d',
      });
      setApiKey(key.raw_key);
      try {
        sessionStorage.setItem(ONBOARDING_KEY_STORAGE, key.raw_key);
      } catch {
        // Storage full/unavailable — key still works for this page view
      }
    } catch (e: any) {
      toast.error(e?.response?.data || 'Failed to create API key');
    } finally {
      setCreatingKey(false);
    }
  };

  // Session guard + load previously saved selections. `validated` stays false
  // (full-screen loader) until the tenant check resolves, so the org-naming
  // step renders first for personal-email signups instead of flashing the
  // integration picker.
  useEffect(() => {
    const init = async () => {
      const isValid = await apiClient.validateSession();
      if (!isValid) {
        sessionStorage.removeItem(ONBOARDING_KEY_STORAGE);
        navigate('/login');
        return;
      }
      const [stateResult, tenantResult] = await Promise.allSettled([
        apiClient.getSetupWizardState(),
        apiClient.getTenant(),
      ]);
      if (stateResult.status === 'fulfilled') {
        const state = stateResult.value;
        if (state.completed) {
          sessionStorage.removeItem(ONBOARDING_KEY_STORAGE);
          navigate('/home');
          return;
        }
        if (state.selected_integrations?.length > 0) {
          setSelected(new Set(state.selected_integrations));
        }
      }
      // Personal-email signups get a random placeholder org name
      // (My-Organization-<6 digits>); offer a rename as the first step.
      // On fetch failure, skip the naming step (non-fatal).
      if (tenantResult.status === 'fulfilled' && tenantResult.value.auto_generated_name) {
        setDefaultOrgName(tenantResult.value.name);
        setStep('org');
      }
      setValidated(true);
    };
    init();
  }, [navigate]);

  const refreshStatuses = async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setStatusLoading(true);
    try {
      const statuses = await apiClient.getWorkflowIntegrationsStatus();
      const connected = new Set(
        statuses.filter((s: any) => s.connected).map((s: any) => s.id as string)
      );
      setConnectedIds(connected);
    } catch {
      // Ignore — statuses stay as-is
    } finally {
      if (!opts?.silent) setStatusLoading(false);
    }
  };

  useEffect(() => {
    if (step === 'browser' || step === 'done') {
      refreshStatuses();
    }
    // While the user connects integrations in other tabs, poll so the
    // checkmarks update without requiring a manual refresh.
    if (step === 'browser') {
      const interval = setInterval(() => refreshStatuses({ silent: true }), 5000);
      return () => clearInterval(interval);
    }
  }, [step]);

  const selectedDefs = useMemo(
    () => INTEGRATION_CATALOG.filter((d) => selected.has(d.key)),
    [selected]
  );

  const toggle = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const persistSelection = async (keys: string[]) => {
    try {
      await apiClient.updateSetupWizardState({ selected_integrations: keys });
    } catch {
      // Non-fatal
    }
  };

  const handlePickContinue = async () => {
    await persistSelection(Array.from(selected));
    setStep('method');
  };

  const finishSetup = async (destination: string) => {
    setSaving(true);
    try {
      await apiClient.updateSetupWizardState({
        completed: true,
        selected_integrations: Array.from(selected),
      });
    } catch {
      // Don't block the user on a failed save
    } finally {
      sessionStorage.removeItem(ONBOARDING_KEY_STORAGE);
      setSaving(false);
      navigate(destination);
    }
  };

  if (!validated) {
    return <PageLoader fullScreen />;
  }

  // The org-naming step is only present for orgs still carrying their
  // auto-generated placeholder name.
  const hasOrgStep = defaultOrgName !== null;
  const baseIndex = { pick: 0, method: 1, browser: 2, agent: 2, cli: 2, done: 3 }[
    step === 'org' ? 'pick' : step
  ];
  const stepIndex = step === 'org' ? 0 : baseIndex + (hasOrgStep ? 1 : 0);
  const totalSteps = hasOrgStep ? 5 : 4;

  return (
    <div className="min-h-screen bg-background">
      {/* Top bar: Kestrel logo (matches sidebar placement) + theme toggle.
          The logo sits 4px below the top padding — halfway between the old
          top-pinned position and dead-center against the taller toggle; the
          toggle is nudged down and inset from the right to line up with
          PageHeader's toggle on the platform pages. */}
      <div className="fixed top-0 left-0 right-0 z-10 flex items-start justify-between px-4 py-4 bg-background">
        <img
          src={isDarkMode ? '/Logo_Kestrel AI_full_dark_mode.svg' : '/Logo_Kestrel AI_full_light_mode.svg'}
          alt="Kestrel AI"
          className="h-5 w-auto mt-1"
        />
        <div className="mt-2 sm:pr-2 lg:pr-4">
          <ModeToggle />
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-20 pb-8 sm:pb-12">
        {/* Progress indicator */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-2">
            {Array.from({ length: totalSteps }, (_, i) => (
              <div
                key={i}
                className={cn(
                  'h-1.5 rounded-full transition-all',
                  i <= stepIndex ? 'bg-primary w-8' : 'bg-muted w-8'
                )}
              />
            ))}
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground"
            disabled={saving}
            onClick={() => finishSetup('/home')}
          >
            Skip setup
          </Button>
        </div>

        {step === 'org' && defaultOrgName !== null && (
          <OrgNameStep
            defaultName={defaultOrgName}
            onDone={(newName) => {
              if (newName) setDefaultOrgName(newName);
              setStep('pick');
            }}
          />
        )}
        {step === 'pick' && (
          <PickStep
            selected={selected}
            toggle={toggle}
            onContinue={handlePickContinue}
          />
        )}
        {step === 'method' && (
          <MethodStep
            selectedCount={selected.size}
            onBack={() => setStep('pick')}
            onSelect={(m) => {
              setMethod(m);
              setStep(m);
            }}
          />
        )}
        {step === 'browser' && (
          <BrowserStep
            defs={selectedDefs}
            connectedIds={connectedIds}
            loading={statusLoading}
            onRefresh={refreshStatuses}
            onBack={() => setStep('method')}
            onContinue={() => setStep('done')}
          />
        )}
        {step === 'agent' && (
          <AgentStep
            selectedKeys={Array.from(selected)}
            apiKey={apiKey}
            creatingKey={creatingKey}
            onCreateKey={createKey}
            onBack={() => setStep('method')}
            onContinue={() => setStep('done')}
          />
        )}
        {step === 'cli' && (
          <CliStep
            defs={selectedDefs}
            apiKey={apiKey}
            creatingKey={creatingKey}
            onCreateKey={createKey}
            onBack={() => setStep('method')}
            onContinue={() => setStep('done')}
          />
        )}
        {step === 'done' && (
          <DoneStep
            defs={selectedDefs}
            connectedIds={connectedIds}
            saving={saving}
            onBack={() => setStep(method ?? 'method')}
            onCreateWorkflow={() => finishSetup('/workflows/new')}
            onSimulated={() => finishSetup('/workflows/new?simulated=1')}
            onGoHome={() => finishSetup('/home')}
          />
        )}
      </div>
    </div>
  );
}

// --- Step 0 (personal-email signups only): name the organization ---

// Mirrors the server's tenant-name rules (validateTenantName in rest.go):
// 1-100 chars of letters, numbers, underscores, dots, and hyphens.
const ORG_NAME_PATTERN = /^[a-zA-Z0-9_.-]+$/;

function validateOrgName(name: string): string | null {
  if (name.length === 0) return 'Organization name is required';
  if (name.length > 100) return 'Organization name must be at most 100 characters';
  if (!ORG_NAME_PATTERN.test(name)) {
    return 'Only letters, numbers, underscores, dots, and hyphens are allowed';
  }
  return null;
}

function OrgNameStep({
  defaultName,
  onDone,
}: {
  defaultName: string;
  onDone: (savedName: string | null) => void;
}) {
  const [name, setName] = useState(defaultName);
  const [saving, setSaving] = useState(false);
  const trimmed = name.trim();
  const validationError = validateOrgName(trimmed);
  const unchanged = trimmed === defaultName;

  const handleContinue = async () => {
    // Keeping the generated default requires no API call.
    if (unchanged) {
      onDone(null);
      return;
    }
    if (validationError) return;
    setSaving(true);
    try {
      const resp = await apiClient.updateTenant({ name: trimmed });
      onDone(resp.name);
    } catch (e: any) {
      toast.error(e?.response?.data || 'Failed to update organization name');
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-foreground mb-2">
          Name your organization
        </h1>
        <p className="text-muted-foreground">
          We generated a name for you — keep it, or pick something more memorable. You can always
          change it later in Settings.
        </p>
      </div>

      <div className="space-y-2">
        <label htmlFor="org-name" className="text-sm font-medium text-foreground">
          Organization name
        </label>
        <Input
          id="org-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={defaultName}
          maxLength={100}
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !validationError && !saving) {
              handleContinue();
            }
          }}
        />
        <p className={cn('text-xs', validationError && !unchanged ? 'text-destructive' : 'text-muted-foreground')}>
          {validationError && !unchanged
            ? validationError
            : 'Letters, numbers, underscores, dots, and hyphens.'}
        </p>
      </div>

      <div className="flex justify-end mt-8">
        <Button onClick={handleContinue} disabled={!!validationError || saving} className="gap-1.5">
          {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          Continue
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

// --- Step 1: pick integrations ---

function PickStep({
  selected,
  toggle,
  onContinue,
}: {
  selected: Set<string>;
  toggle: (key: string) => void;
  onContinue: () => void;
}) {
  const grouped = groupByCategory(INTEGRATION_CATALOG);

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-foreground mb-2">
          Welcome to Kestrel
        </h1>
        <p className="text-muted-foreground">
          Your 14-day Workflows trial has started. Pick the tools in your platform stack — we'll
          walk you through connecting them. You can always add more later.
        </p>
      </div>

      <div className="space-y-6">
        {Array.from(grouped.entries()).map(([category, defs]) => (
          <div key={category}>
            <h2 className="text-sm font-medium text-muted-foreground mb-2">{category}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {defs.map((def) => {
                const isSelected = selected.has(def.key);
                return (
                  <button
                    key={def.key}
                    type="button"
                    onClick={() => toggle(def.key)}
                    className={cn(
                      'flex items-start gap-3 rounded-lg border p-3 text-left transition-colors overflow-hidden',
                      isSelected
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:bg-accent'
                    )}
                  >
                    <div className="flex-shrink-0 mt-0.5">
                      {isSelected ? (
                        <CheckCircle2 className="h-4 w-4 text-primary" />
                      ) : (
                        <Circle className="h-4 w-4 text-muted-foreground/40" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <IntegrationIcon integration={def.key} size={14} className="flex-shrink-0" />
                        <p className="text-sm font-medium text-foreground truncate">{def.name}</p>
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                        {def.description}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between mt-8">
        <p className="text-sm text-muted-foreground">
          {selected.size === 0
            ? 'Select at least one integration'
            : `${selected.size} selected`}
        </p>
        <Button onClick={onContinue} disabled={selected.size === 0} className="gap-1.5">
          Continue
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

// --- Step 2: choose setup method ---

function MethodStep({
  selectedCount,
  onBack,
  onSelect,
}: {
  selectedCount: number;
  onBack: () => void;
  onSelect: (m: SetupMethod) => void;
}) {
  const options: {
    id: SetupMethod;
    title: string;
    description: string;
    icon: React.ComponentType<{ className?: string }>;
    badge?: string;
  }[] = [
    {
      id: 'browser',
      title: 'In the browser',
      description:
        'Step through each integration page and paste in credentials yourself.',
      icon: Globe,
    },
    {
      id: 'cli',
      title: 'Kestrel CLI',
      description:
        'Run kestrel integrations connect commands yourself from the terminal.',
      icon: SquareTerminal,
    },
    {
      id: 'agent',
      title: 'AI coding agent',
      description:
        'Copy a prompt into your coding agent and let it walk you through the setup.',
      icon: Bot,
    },
  ];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-foreground mb-2">
          How do you want to connect them?
        </h1>
        <p className="text-muted-foreground">
          {selectedCount} integration{selectedCount === 1 ? '' : 's'} to set up. Pick whichever
          workflow suits you.
        </p>
      </div>

      <div className="space-y-3">
        {options.map((opt) => (
          <button
            key={opt.id}
            type="button"
            onClick={() => onSelect(opt.id)}
            className="w-full flex items-start gap-4 rounded-lg border border-border p-4 text-left transition-colors hover:bg-accent hover:border-primary/40 overflow-hidden"
          >
            <div className="flex-shrink-0 rounded-md bg-muted p-2.5">
              <opt.icon className="h-5 w-5 text-foreground" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium text-foreground">{opt.title}</p>
                {opt.badge && (
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                    {opt.badge}
                  </Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground mt-1">{opt.description}</p>
            </div>
            <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-1" />
          </button>
        ))}
      </div>

      <div className="mt-8">
        <Button variant="ghost" onClick={onBack} className="gap-1.5 text-muted-foreground">
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
      </div>
    </div>
  );
}

// --- Step 3a: browser stepper ---

function BrowserStep({
  defs,
  connectedIds,
  loading,
  onRefresh,
  onBack,
  onContinue,
}: {
  defs: IntegrationDef[];
  connectedIds: Set<string>;
  loading: boolean;
  onRefresh: () => void;
  onBack: () => void;
  onContinue: () => void;
}) {
  const connectedCount = defs.filter((d) => connectedIds.has(statusIdFor(d))).length;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-foreground mb-2">Connect your integrations</h1>
        <p className="text-muted-foreground">
          Open each integration in a new tab, connect it, then come back here. Status updates
          automatically.
        </p>
      </div>

      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-muted-foreground">
          {connectedCount} of {defs.length} connected
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={onRefresh}
          disabled={loading}
          aria-label="Refresh status"
          title="Refresh status"
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
        </Button>
      </div>

      <div className="space-y-2">
        {defs.map((def) => {
          const connected = connectedIds.has(statusIdFor(def));
          return (
            <div
              key={def.key}
              className="flex items-center gap-3 rounded-lg border border-border p-3 overflow-hidden"
            >
              <div className="flex-shrink-0">
                {connected ? (
                  <CheckCircle2 className="h-4 w-4 text-foreground" />
                ) : (
                  <Circle className="h-4 w-4 text-muted-foreground/40" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <IntegrationIcon integration={def.key} size={14} className="flex-shrink-0" />
                  <p className={cn('text-sm font-medium truncate', connected ? 'text-muted-foreground line-through' : 'text-foreground')}>
                    {def.name}
                  </p>
                </div>
                <p className="text-xs text-muted-foreground truncate">{def.description}</p>
              </div>
              <Button
                variant={connected ? 'ghost' : 'outline'}
                size="sm"
                asChild
                className="flex-shrink-0 gap-1.5"
              >
                <a href={def.href} target="_blank" rel="noopener noreferrer">
                  {connected ? 'View' : 'Connect'}
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </Button>
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between mt-8">
        <Button variant="ghost" onClick={onBack} className="gap-1.5 text-muted-foreground">
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
        <Button onClick={onContinue} className="gap-1.5">
          Continue
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

// --- Step 3b: AI coding agent ---

function AgentStep({
  selectedKeys,
  apiKey,
  creatingKey,
  onCreateKey,
  onBack,
  onContinue,
}: {
  selectedKeys: string[];
  apiKey: string | null;
  creatingKey: boolean;
  onCreateKey: () => void;
  onBack: () => void;
  onContinue: () => void;
}) {
  const prompt = useMemo(() => generateAgentPrompt(selectedKeys), [selectedKeys]);
  const installCommand = 'brew install KestrelAI/tap/kestrel';
  const authCommand = apiKey
    ? `kestrel auth ${apiKey} --server ${window.location.origin}`
    : null;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-foreground mb-2">Set up with an AI coding agent</h1>
        <p className="text-muted-foreground">
          Install and authenticate the Kestrel CLI in your terminal, then hand the prompt below to
          Cursor, Claude Code, or any coding agent. It will connect your integrations — and any
          secrets are entered directly into the CLI, never shown to the agent.
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <p className="text-sm font-medium text-foreground mb-2">1. Install the Kestrel CLI</p>
          <CodeBlock copyText={installCommand}>{installCommand}</CodeBlock>
          <p className="text-xs text-muted-foreground mt-2">
            Run this in your terminal. Other install options are on{' '}
            <a
              href="https://github.com/KestrelAI/kestrel-cli"
              target="_blank"
              rel="noreferrer"
              className="underline hover:text-foreground"
            >
              GitHub
            </a>
            .
          </p>
        </div>

        <div>
          <p className="text-sm font-medium text-foreground mb-2">2. Authenticate the CLI</p>
          {authCommand ? (
            <>
              <CodeBlock copyText={authCommand}>{authCommand}</CodeBlock>
              <p className="text-xs text-muted-foreground mt-2">
                Run this in your terminal — the API key (scoped to integrations and workflows,
                expires in 30 days) is included. Keep it out of your agent chat.
              </p>
            </>
          ) : (
            <>
              <Button size="sm" onClick={onCreateKey} disabled={creatingKey} className="gap-1.5">
                {creatingKey && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Create API key
              </Button>
              <p className="text-xs text-muted-foreground mt-2">
                Creates an API key scoped to integrations and workflows, expiring in 30 days, and
                gives you the exact auth command to run in your terminal.
              </p>
            </>
          )}
        </div>

        <div>
          <p className="text-sm font-medium text-foreground mb-2">3. Give this prompt to your agent</p>
          <div className="flex items-start gap-2 bg-muted rounded-md p-3">
            <pre className="flex-1 min-w-0 text-xs font-mono text-foreground overflow-auto max-h-80 whitespace-pre-wrap">
              {prompt}
            </pre>
            <CopyIconButton text={prompt} />
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Personalized for your {selectedKeys.length} selected integration
            {selectedKeys.length === 1 ? '' : 's'}. It contains no secrets — the agent uses your
            already-authenticated CLI, and credentials are entered via hidden CLI prompts.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between mt-8">
        <Button variant="ghost" onClick={onBack} className="gap-1.5 text-muted-foreground">
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
        <Button onClick={onContinue} className="gap-1.5">
          Continue
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

// --- Step 3c: manual CLI ---

function CliStep({
  defs,
  apiKey,
  creatingKey,
  onCreateKey,
  onBack,
  onContinue,
}: {
  defs: IntegrationDef[];
  apiKey: string | null;
  creatingKey: boolean;
  onCreateKey: () => void;
  onBack: () => void;
  onContinue: () => void;
}) {
  const authCommand = apiKey
    ? `kestrel auth ${apiKey} --server ${window.location.origin}`
    : null;
  // Bare commands with no placeholders/brackets/comments so the block can be
  // pasted straight into a shell — zsh chokes on `<...>`, `[...]`, and `#`
  // comment lines. The CLI prompts for every required value with setup help.
  const allCommands = defs
    .map((d) => bareConnectCommand(d))
    .filter((cmd): cmd is string => !!cmd)
    .join('\n');
  const hasSecrets = defs.some((d) => (d.fields || []).some((f) => f.secret));

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-foreground mb-2">Set up with the Kestrel CLI</h1>
        <p className="text-muted-foreground">
          Install the CLI, authenticate with an API key, then connect each integration from your
          terminal.
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <p className="text-sm font-medium text-foreground mb-2">1. Install</p>
          <CodeBlock copyText="brew install KestrelAI/tap/kestrel">brew install KestrelAI/tap/kestrel</CodeBlock>
        </div>

        <div>
          <p className="text-sm font-medium text-foreground mb-2">2. Authenticate</p>
          {authCommand ? (
            <>
              <CodeBlock copyText={authCommand}>{authCommand}</CodeBlock>
              <p className="text-xs text-muted-foreground mt-2">
                Run this in your terminal — the API key (scoped to integrations and workflows,
                expires in 30 days) is included. It's also listed on your{' '}
                <a href="/api-keys" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">
                  API Keys
                </a>{' '}
                page as "Onboarding".
              </p>
            </>
          ) : (
            <>
              <Button size="sm" onClick={onCreateKey} disabled={creatingKey} className="gap-1.5">
                {creatingKey && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Create API key
              </Button>
              <p className="text-xs text-muted-foreground mt-2">
                Creates an API key scoped to integrations and workflows, expiring in 30 days, and
                gives you the exact auth command to run in your terminal.
              </p>
            </>
          )}
        </div>

        <div>
          <p className="text-sm font-medium text-foreground mb-2">3. Connect your integrations</p>
          <CodeBlock copyText={allCommands}>{allCommands}</CodeBlock>
          <p className="text-xs text-muted-foreground mt-2">
            Paste these into your terminal — each command walks you through setup, prompting for
            what it needs{hasSecrets ? ' (secrets are entered with hidden input, never as flags)' : ''}.
            Run <code className="bg-muted px-1 rounded">kestrel integrations list</code> to
            check status and{' '}
            <code className="bg-muted px-1 rounded">kestrel integrations connect &lt;name&gt; --help</code>{' '}
            to see all flags for an integration.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between mt-8">
        <Button variant="ghost" onClick={onBack} className="gap-1.5 text-muted-foreground">
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
        <Button onClick={onContinue} className="gap-1.5">
          Continue
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

// --- Step 4: done / first workflow ---

// Status panel echoing `kestrel integrations list`, styled like the wizard's
// existing code blocks (bg-muted, mono) rather than a faux terminal window.
function IntegrationStatusTerminal({
  defs,
  connectedIds,
}: {
  defs: IntegrationDef[];
  connectedIds: Set<string>;
}) {
  if (defs.length === 0) return null;
  const connectedCount = defs.filter((d) => connectedIds.has(statusIdFor(d))).length;

  return (
    <div className="rounded-lg border border-border p-3 font-mono text-xs">
      <p className="mb-3">
        <span className="text-muted-foreground">❯</span>{' '}
        <span className="text-foreground">kestrel integrations list</span>
      </p>
      <div className="space-y-2">
        {defs.map((def) => {
          const connected = connectedIds.has(statusIdFor(def));
          return (
            <div key={def.key} className="flex items-center gap-2 min-w-0">
              <IntegrationIcon
                integration={def.key}
                size={14}
                className={cn('flex-shrink-0', !connected && 'opacity-40')}
              />
              <span
                className={cn(
                  'truncate',
                  connected ? 'text-foreground' : 'text-muted-foreground'
                )}
              >
                {def.name}
              </span>
              <span
                className="flex-1 min-w-4 border-b border-dotted border-border"
                aria-hidden
              />
              {connected ? (
                <span className="flex items-center gap-1.5 flex-shrink-0 text-emerald-600 dark:text-emerald-400">
                  <Check className="h-3.5 w-3.5" />
                  connected
                </span>
              ) : (
                <span className="flex-shrink-0 text-muted-foreground">
                  not connected
                </span>
              )}
            </div>
          );
        })}
      </div>
      <p className="mt-3 text-muted-foreground">
        {connectedCount} of {defs.length} connected
      </p>
    </div>
  );
}

function DoneStep({
  defs,
  connectedIds,
  saving,
  onBack,
  onCreateWorkflow,
  onSimulated,
  onGoHome,
}: {
  defs: IntegrationDef[];
  connectedIds: Set<string>;
  saving: boolean;
  onBack: () => void;
  onCreateWorkflow: () => void;
  onSimulated: () => void;
  onGoHome: () => void;
}) {
  const connectedDefs = defs.filter((d) => connectedIds.has(statusIdFor(d)));
  // Feature an example prompt for something the user actually connected;
  // fall back to their selection, then a generic example.
  const exampleSource = [...connectedDefs, ...defs].find((d) => d.workflowExample);
  const workflowExample =
    exampleSource?.workflowExample ??
    'When a deployment fails, run an RCA and post the summary to Slack.';

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-foreground mb-2">You're all set</h1>
        <p className="text-muted-foreground">
          Your home dashboard tracks the rest of your setup. The best next step: create your first
          workflow — describe what you want to automate in plain English and Kestrel builds it for you.
        </p>
      </div>

      <div className="mb-6">
        <IntegrationStatusTerminal defs={defs} connectedIds={connectedIds} />
      </div>

      <div className="space-y-3">
        <button
          type="button"
          onClick={onCreateWorkflow}
          disabled={saving}
          className="w-full flex items-start gap-4 rounded-lg border border-primary/40 bg-primary/5 p-4 text-left transition-colors hover:bg-primary/10 overflow-hidden disabled:opacity-60"
        >
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-foreground">Create your first workflow</p>
            <p className="text-sm text-muted-foreground mt-1">
              e.g. "{workflowExample}"
            </p>
          </div>
          <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-1" />
        </button>

        <button
          type="button"
          onClick={onSimulated}
          disabled={saving}
          className="w-full flex items-start gap-4 rounded-lg border border-border p-4 text-left transition-colors hover:bg-accent overflow-hidden disabled:opacity-60"
        >
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-foreground">Try workflows in simulated mode</p>
            <p className="text-sm text-muted-foreground mt-1">
              Build and test workflows as if every integration were connected.
            </p>
          </div>
          <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-1" />
        </button>

        <button
          type="button"
          onClick={onGoHome}
          disabled={saving}
          className="w-full flex items-start gap-4 rounded-lg border border-border p-4 text-left transition-colors hover:bg-accent overflow-hidden disabled:opacity-60"
        >
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-foreground">Go to my dashboard</p>
            <p className="text-sm text-muted-foreground mt-1">
              Explore the platform — your setup checklist stays on the home page.
            </p>
          </div>
          <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-1" />
        </button>
      </div>

      <div className="mt-8">
        <Button
          variant="ghost"
          onClick={onBack}
          disabled={saving}
          className="gap-1.5 text-muted-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
      </div>
    </div>
  );
}

export default SetupWizard;
